// SCR-STAFF-802 (AP-CMP-01؛ US-CMP-020، 021، 023، 025؛ AC-CMP-001.3): دفتر سهم با صف «بدون قرارداد» و «نیازمند بررسی»، جزئیات قلم (قاعده، نسخه قرارداد،
// مبنا و محاسبه هر ذی‌نفع و سهم مرکز)، بازمحاسبه، پذیرش NeedsReview با دلیل و اصلاح دستی با دلیل (BR-CMP-025؛ ردیف‌ها هرگز ویرایش یا حذف نمی‌شوند).
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type CmpAdjustmentRequest,
  type CmpShareEntryKind,
  type CmpShareEntryStatus,
  type CmpShareEntryView,
  type CmpShareLineView,
  type CmpShareSourceKind,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DataTable,
  ErrorState,
  FormDialog,
  JalaliDate,
  KeyValueList,
  ListPage,
  Money,
  MoneyFieldRHF,
  ReasonDialog,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import {
  compensationKeys,
  ENTRY_KINDS,
  LINE_SOURCES,
  LINE_STATUSES,
  ledgerScopes,
  lineTone,
  ProviderPicker,
  useCalculationText,
} from './common';

export const SCR_STAFF_802 = 'SCR-STAFF-802';

export function LedgerPage() {
  const { t } = useTranslation('cmp');
  const access = useAccess();
  const write = useWriteAccess('cmp.ledger.adjust');
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'entries' ? 'entries' : 'lines';
  const [adjusting, setAdjusting] = useState<{ lineId: string | null; providerId: string | null } | null>(
    null,
  );
  const canAdjust = access.can('cmp.ledger.adjust') && write.canWrite;

  return (
    <ListPage
      screenId={SCR_STAFF_802}
      titleKey="cmp:ledger.title"
      subtitleKey="cmp:ledger.subtitle"
      actions={
        access.can('cmp.ledger.adjust') ? (
          <Button
            variant="contained"
            onClick={() => setAdjusting({ lineId: null, providerId: null })}
            disabled={!write.canWrite}
            data-testid="ledger-adjust"
          >
            {t('cmp:ledger.adjust')}
          </Button>
        ) : undefined
      }
      filters={
        <Stack spacing={1}>
          {access.can('cmp.ledger.adjust') && <WriteNotice write={write} />}
          <Tabs
            value={tab}
            onChange={(_, value: string) => {
              const next = new URLSearchParams(params);
              next.set('tab', value);
              setParams(next, { replace: true });
            }}
          >
            <Tab value="lines" label={t('cmp:ledger.lines_tab')} data-testid="ledger-tab-lines" />
            <Tab value="entries" label={t('cmp:ledger.entries_tab')} data-testid="ledger-tab-entries" />
          </Tabs>
        </Stack>
      }
    >
      {tab === 'lines' ? (
        <LinesTable
          canAdjust={canAdjust}
          onAdjust={(lineId, providerId) => setAdjusting({ lineId, providerId })}
        />
      ) : (
        <EntriesTable />
      )}
      {adjusting && (
        <AdjustmentDialog
          lineId={adjusting.lineId}
          providerId={adjusting.providerId}
          onClose={() => setAdjusting(null)}
        />
      )}
    </ListPage>
  );
}

function LinesTable({
  canAdjust,
  onAdjust,
}: {
  canAdjust: boolean;
  onAdjust: (lineId: string, providerId: string | null) => void;
}) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? '') as CmpShareEntryStatus | '';
  const [source, setSource] = useState<CmpShareSourceKind | ''>('');
  const [providerId, setProviderId] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [openId, setOpenId] = useState<string | null>(null);

  const lines = useQuery({
    queryKey: [...compensationKeys.lines(session?.tenantId), status, source, providerId, cursor],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/compensation/share-lines', {
          params: {
            query: {
              status: status || undefined,
              source: source || undefined,
              providerId: providerId ?? undefined,
              cursor,
              limit: 50,
            },
          },
        }),
      ),
  });

  const columns: DataTableColumn<CmpShareLineView>[] = [
    {
      id: 'date',
      headerKey: 'cmp:ledger.columns.date',
      cell: (row) => <JalaliDate value={row.businessDate} />,
    },
    { id: 'service', headerKey: 'cmp:ledger.columns.service', cell: (row) => row.serviceName ?? '—' },
    { id: 'provider', headerKey: 'cmp:ledger.columns.provider', cell: (row) => row.providerName ?? '—' },
    { id: 'source', headerKey: 'cmp:ledger.columns.source', cell: (row) => t(`cmp:source.${row.source}`) },
    {
      id: 'status',
      headerKey: 'cmp:ledger.columns.status',
      cell: (row) => (
        <Stack spacing={0.5}>
          <StatusChip tone={lineTone(row.status)} labelKey={`cmp:line_status.${row.status}`} />
          {row.unassignedReason && (
            <Typography variant="caption" color="text.secondary">
              {t(`cmp:unassigned_reason.${row.unassignedReason}`)}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      id: 'base',
      headerKey: 'cmp:ledger.columns.base',
      cell: (row) => <Money amountMinor={Number(row.netBase.amount)} />,
      align: 'right',
    },
    {
      id: 'providerShare',
      headerKey: 'cmp:ledger.columns.provider_share',
      cell: (row) => <Money amountMinor={Number(row.providerShare.amount)} />,
      align: 'right',
    },
    {
      id: 'centerShare',
      headerKey: 'cmp:ledger.columns.center_share',
      cell: (row) => <Money amountMinor={Number(row.centerShare.amount)} />,
      align: 'right',
    },
    {
      id: 'actions',
      headerKey: 'cmp:ledger.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setOpenId(row.id)} data-testid={`ledger-line-open-${row.id}`}>
          {t('cmp:ledger.details')}
        </Button>
      ),
    },
  ];

  const setStatus = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) {
      next.set('status', value);
    } else {
      next.delete('status');
    }
    setParams(next, { replace: true });
    setCursor(undefined);
  };

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label={t('cmp:ledger.filters.status')}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          sx={{ minWidth: 180 }}
          slotProps={{ htmlInput: { 'data-testid': 'ledger-filter-status' } }}
        >
          <MenuItem value="">{t('cmp:ledger.filters.all')}</MenuItem>
          {LINE_STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`cmp:line_status.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('cmp:ledger.filters.source')}
          value={source}
          onChange={(event) => {
            setCursor(undefined);
            setSource(event.target.value as CmpShareSourceKind | '');
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('cmp:ledger.filters.all')}</MenuItem>
          {LINE_SOURCES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`cmp:source.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <ProviderPicker
          value={providerId}
          onChange={(id) => {
            setCursor(undefined);
            setProviderId(id);
          }}
          labelKey="cmp:ledger.filters.provider"
          testId="ledger-filter-provider"
        />
      </Stack>
      <DataTable
        captionKey="cmp:ledger.caption_lines"
        columns={columns}
        rows={lines.data?.items}
        getRowId={(row) => row.id}
        isLoading={lines.isPending}
        error={lines.error}
        onRetry={() => void lines.refetch()}
        hasMore={Boolean(lines.data?.nextCursor)}
        onLoadMore={() => setCursor(lines.data?.nextCursor ?? undefined)}
        emptyTitleKey="cmp:ledger.empty_lines"
      />
      {openId && (
        <LineDetailDialog
          id={openId}
          canAdjust={canAdjust}
          onAdjust={onAdjust}
          onClose={() => setOpenId(null)}
        />
      )}
    </>
  );
}

/** جزئیات قلم: ابعاد، تخفیف‌ها و همه ردیف‌ها با محاسبه (AC-CMP-020.1). */
function LineDetailDialog({
  id,
  canAdjust,
  onAdjust,
  onClose,
}: {
  id: string;
  canAdjust: boolean;
  onAdjust: (lineId: string, providerId: string | null) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const calculation = useCalculationText();
  const [accepting, setAccepting] = useState(false);

  const line = useQuery({
    queryKey: compensationKeys.line(session?.tenantId, id),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/compensation/share-lines/{id}', { params: { path: { id } } })),
  });

  const refresh = (view: CmpShareLineView) => {
    queryClient.setQueryData(compensationKeys.line(session?.tenantId, id), view);
    for (const key of ledgerScopes(session?.tenantId)) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };

  const recalculate = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/compensation/share-lines/{id}:recalculate', { params: { path: { id } } }),
      ),
    onSuccess: refresh,
  });

  const accept = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        tenantApi.POST('/api/v1/compensation/share-lines/{id}:accept', {
          params: { path: { id } },
          body: { reason },
        }),
      ),
    onSuccess: (view) => {
      refresh(view);
      setAccepting(false);
    },
  });

  const view = line.data;
  const pending = view?.status === 'Unassigned' || view?.status === 'NeedsReview';

  const columns: DataTableColumn<CmpShareEntryView>[] = [
    {
      id: 'beneficiary',
      headerKey: 'cmp:ledger.columns.beneficiary',
      cell: (row) => (row.beneficiary === 'Center' ? t('cmp:ledger.center') : (row.providerName ?? '—')),
    },
    {
      id: 'role',
      headerKey: 'cmp:ledger.columns.role',
      cell: (row) => (row.role ? t(`cmp:role.${row.role}`) : '—'),
    },
    { id: 'kind', headerKey: 'cmp:ledger.columns.kind', cell: (row) => <EntryKind row={row} /> },
    {
      id: 'status',
      headerKey: 'cmp:ledger.columns.status',
      cell: (row) => <StatusChip tone={lineTone(row.status)} labelKey={`cmp:line_status.${row.status}`} />,
    },
    {
      id: 'calculation',
      headerKey: 'cmp:ledger.columns.calculation',
      cell: (row) => calculation(row) || row.reason || '—',
    },
    {
      id: 'amount',
      headerKey: 'cmp:ledger.columns.amount',
      cell: (row) => <Money amountMinor={Number(row.amount.amount)} />,
      align: 'right',
    },
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="share-line-title"
      data-testid="share-line-detail"
    >
      <DialogTitle id="share-line-title">{t('cmp:line_detail.title')}</DialogTitle>
      <DialogContent dividers>
        {line.error ? (
          <ErrorState error={line.error} onRetry={() => void line.refetch()} />
        ) : (
          view && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <StatusChip tone={lineTone(view.status)} labelKey={`cmp:line_status.${view.status}`} />
                <Typography variant="body2">
                  {view.serviceName ?? t(`cmp:source.${view.source}`)} · {view.providerName ?? '—'} ·{' '}
                  <JalaliDate value={view.businessDate} />
                </Typography>
              </Stack>
              <KeyValueList
                items={[
                  {
                    labelKey:
                      view.source === 'Service' ? 'cmp:line_detail.gross' : 'cmp:line_detail.session_value',
                    value: (
                      <Money
                        amountMinor={Number(
                          view.source === 'Service' ? view.gross.amount : view.sessionValue.amount,
                        )}
                      />
                    ),
                  },
                  {
                    labelKey: 'cmp:line_detail.discounts',
                    value: view.discounts.length
                      ? view.discounts.map((discount, index) => (
                          <span key={index}>
                            {t(`cmp:discount_source.${discount.source}`)}{' '}
                            <Money amountMinor={Number(discount.amount.amount)} />{' '}
                          </span>
                        ))
                      : t('cmp:line_detail.no_discounts'),
                  },
                  { labelKey: 'cmp:line_detail.tax', value: <Money amountMinor={Number(view.tax.amount)} /> },
                  {
                    labelKey: 'cmp:line_detail.line_base',
                    value: <Money amountMinor={Number(view.lineBase.amount)} />,
                  },
                  {
                    labelKey: 'cmp:line_detail.net_base',
                    value: <Money amountMinor={Number(view.netBase.amount)} />,
                  },
                ]}
              />
              <DataTable
                captionKey="cmp:line_detail.entries"
                columns={columns}
                rows={view.entries}
                getRowId={(row) => row.id}
              />
              {(recalculate.error ?? accept.error) ? (
                <ErrorState error={recalculate.error ?? accept.error} />
              ) : null}
            </Stack>
          )
        )}
      </DialogContent>
      <DialogActions>
        {canAdjust && view && (
          <>
            {pending && (
              <Button
                onClick={() => recalculate.mutate()}
                disabled={recalculate.isPending}
                data-testid="share-line-recalculate"
              >
                {t('cmp:ledger.recalculate')}
              </Button>
            )}
            {view.status === 'NeedsReview' && (
              <Button color="warning" onClick={() => setAccepting(true)} data-testid="share-line-accept">
                {t('cmp:ledger.accept')}
              </Button>
            )}
            <Button
              onClick={() => onAdjust(view.id, view.providerId ?? null)}
              data-testid="share-line-adjust"
            >
              {t('cmp:line_detail.adjust_line')}
            </Button>
          </>
        )}
        <Button onClick={onClose}>{t('cmp:detail.close')}</Button>
      </DialogActions>
      <ReasonDialog
        open={accepting}
        titleKey="cmp:ledger.accept_title"
        messageKey="cmp:ledger.accept_message"
        confirmKey="cmp:ledger.accept"
        destructive={false}
        busy={accept.isPending}
        error={accept.error ?? undefined}
        onConfirm={(reason) => accept.mutate(reason)}
        onCancel={() => {
          accept.reset();
          setAccepting(false);
        }}
      />
    </Dialog>
  );
}

function EntryKind({ row }: { row: CmpShareEntryView }) {
  const { t } = useTranslation('cmp');
  return row.manual ? (
    <Chip size="small" color="secondary" label={t('cmp:ledger.manual')} data-testid="entry-manual-chip" />
  ) : (
    <>{t(`cmp:entry_kind.${row.kind}`)}</>
  );
}

function EntriesTable() {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const [kind, setKind] = useState<CmpShareEntryKind | ''>('');
  const [providerId, setProviderId] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const calculation = useCalculationText();

  const entries = useQuery({
    queryKey: [...compensationKeys.entries(session?.tenantId), kind, providerId, cursor],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/compensation/share-entries', {
          params: {
            query: { kind: kind || undefined, providerId: providerId ?? undefined, cursor, limit: 50 },
          },
        }),
      ),
  });

  const columns: DataTableColumn<CmpShareEntryView>[] = [
    {
      id: 'date',
      headerKey: 'cmp:ledger.columns.date',
      cell: (row) => <JalaliDate value={row.businessDate} />,
    },
    {
      id: 'beneficiary',
      headerKey: 'cmp:ledger.columns.beneficiary',
      cell: (row) => (row.beneficiary === 'Center' ? t('cmp:ledger.center') : (row.providerName ?? '—')),
    },
    {
      id: 'role',
      headerKey: 'cmp:ledger.columns.role',
      cell: (row) => (row.role ? t(`cmp:role.${row.role}`) : '—'),
    },
    { id: 'kind', headerKey: 'cmp:ledger.columns.kind', cell: (row) => <EntryKind row={row} /> },
    {
      id: 'status',
      headerKey: 'cmp:ledger.columns.status',
      cell: (row) => <StatusChip tone={lineTone(row.status)} labelKey={`cmp:line_status.${row.status}`} />,
    },
    {
      id: 'calculation',
      headerKey: 'cmp:ledger.columns.reason',
      cell: (row) => row.reason ?? (calculation(row) || '—'),
    },
    {
      id: 'amount',
      headerKey: 'cmp:ledger.columns.amount',
      cell: (row) => <Money amountMinor={Number(row.amount.amount)} />,
      align: 'right',
    },
  ];

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label={t('cmp:ledger.filters.kind')}
          value={kind}
          onChange={(event) => {
            setCursor(undefined);
            setKind(event.target.value as CmpShareEntryKind | '');
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('cmp:ledger.filters.all')}</MenuItem>
          {ENTRY_KINDS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`cmp:entry_kind.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <ProviderPicker
          value={providerId}
          onChange={(id) => {
            setCursor(undefined);
            setProviderId(id);
          }}
          labelKey="cmp:ledger.filters.provider"
          testId="entries-filter-provider"
        />
      </Stack>
      <DataTable
        captionKey="cmp:ledger.caption_entries"
        columns={columns}
        rows={entries.data?.items}
        getRowId={(row) => row.id}
        isLoading={entries.isPending}
        error={entries.error}
        onRetry={() => void entries.refetch()}
        hasMore={Boolean(entries.data?.nextCursor)}
        onLoadMore={() => setCursor(entries.data?.nextCursor ?? undefined)}
        emptyTitleKey="cmp:ledger.empty_entries"
      />
    </>
  );
}

const adjustmentSchema = z.object({
  providerId: z
    .string()
    .nullable()
    .refine((value) => Boolean(value), { error: 'common:validation.required' }),
  direction: z.enum(['increase', 'decrease']),
  amount: z
    .number({ error: 'common:validation.required' })
    .int({ error: 'common:validation.money' })
    .positive({ error: 'common:validation.money' })
    .nullable()
    .refine((value) => value !== null, { error: 'common:validation.required' }),
  reason: zRequiredText(500),
});
type AdjustmentForm = z.input<typeof adjustmentSchema>;

/** اصلاح دستی مثبت یا منفی با دلیل الزامی (US-CMP-025)، اختیاری پیوسته به قلم. */
function AdjustmentDialog({
  lineId,
  providerId,
  onClose,
}: {
  lineId: string | null;
  providerId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<AdjustmentForm>({
    resolver: zodResolver(adjustmentSchema),
    defaultValues: { providerId, direction: 'increase', amount: null, reason: '' },
  });

  const save = useMutation({
    mutationFn: (form: AdjustmentForm) => {
      const amount = (form.amount ?? 0) * (form.direction === 'decrease' ? -1 : 1);
      const body: CmpAdjustmentRequest = {
        providerId: form.providerId ?? '',
        amount: { amount, currency: 'IRR' },
        reason: form.reason.trim(),
        lineId,
        branchId: null,
      };
      return unwrap(
        tenantApi.POST('/api/v1/compensation/adjustments', {
          body,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      );
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      for (const key of ledgerScopes(session?.tenantId)) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['providerId', 'amount', 'reason'],
        codeFields: { 'PHX-CMP-0105': 'reason' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-802-ADJUST"
      titleKey="cmp:adjust.title"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      {lineId && (
        <Typography variant="body2" color="text.secondary">
          {t('cmp:adjust.line_hint')}
        </Typography>
      )}
      <Controller
        control={control}
        name="providerId"
        render={({ field, fieldState }) => (
          <ProviderPicker
            value={field.value ?? null}
            onChange={field.onChange}
            labelKey="cmp:adjust.provider"
            testId="adjust-provider"
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message ? t(fieldState.error.message) : undefined}
          />
        )}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="direction"
          labelKey="cmp:adjust.direction"
          fullWidth
          options={[
            { value: 'increase', labelKey: 'cmp:adjust.increase' },
            { value: 'decrease', labelKey: 'cmp:adjust.decrease' },
          ]}
        />
        <MoneyFieldRHF control={control} name="amount" labelKey="cmp:adjust.amount" required fullWidth />
      </Stack>
      <TextFieldRHF
        control={control}
        name="reason"
        labelKey="cmp:adjust.reason"
        required
        fullWidth
        multiline
        minRows={2}
        slotProps={{ htmlInput: { 'data-testid': 'adjust-reason' } }}
      />
    </FormDialog>
  );
}
